// Direct ODP (Zaius) reporting.
//
// Every other ODP event on this site is reported indirectly: pushed onto the GTM dataLayer
// (see analytics-helper) and converted to an ODP call by a tag in the GTM container. The
// customer/login event is the exception — it carries the person's GUID as `_person_id`, which
// the existing `zaiusEvent` dataLayer contract has no slot for, so it calls zaius directly.
//
// `window.zaius` is injected by GTM (zaius-min.js), asynchronously and after this code may
// first run, so the call is guarded and retried rather than assumed. If GTM never loads (e.g.
// blocked by a consent tool), every function here is a silent no-op — analytics must never
// break the page.

const FIRED_KEY = 'odpLoginFired';
const CUSTOMER_EVENT = 'customer';
const LOGIN_ACTION = 'login';

// GTM typically has zaius-min.js in place well inside a second; 20 x 250ms is a generous
// ceiling after which we conclude it isn't coming (consent block, network failure).
const RETRY_INTERVAL_MS = 250;
const MAX_ATTEMPTS = 20;

// Fallback marker for when sessionStorage is unavailable (Safari private mode, storage
// disabled by policy). Only dedupes within the current document, but that is strictly better
// than firing on every React re-render.
let firedInThisDocument: string = null;

// Guards against a second retry loop being started by a re-render while the first is still
// waiting for zaius to appear.
let pendingGuid: string = null;

// Handle for the in-flight retry loop, so sign-out can cancel it. Without this, a loop still
// waiting for zaius could report a login for a user who has already signed out — and worse,
// re-identify the browser immediately after anonymize() had reset it.
let retryTimer: number = null;

const getZaius = (): any => {
	if (typeof window === 'undefined') {
		return null;
	}
	return (window as any).zaius;
};

const readMarker = (): string => {
	if (firedInThisDocument) {
		return firedInThisDocument;
	}
	try {
		return window.sessionStorage?.getItem(FIRED_KEY);
	} catch {
		return null;
	}
};

const writeMarker = (personGuid: string) => {
	firedInThisDocument = personGuid;
	try {
		window.sessionStorage?.setItem(FIRED_KEY, personGuid);
	} catch { /* non-fatal: the in-document marker still dedupes this page */ }
};

/**
 * Attempts the ODP call. Returns false when zaius isn't available yet (or the call threw),
 * so the caller can decide whether to retry — the marker is only written on success, meaning
 * a consent-blocked first page view doesn't permanently suppress the event.
 */
const sendLogin = (personGuid: string): boolean => {
	const zaius = getZaius();
	if (!zaius?.event) {
		return false;
	}

	try {
		zaius.event(CUSTOMER_EVENT, {
			action: LOGIN_ACTION,
			_person_id: personGuid,
		});
		return true;
	} catch {
		return false;
	}
};

/** Stops any in-flight retry loop and releases its guard. */
const cancelRetry = (): void => {
	pendingGuid = null;

	if (retryTimer !== null) {
		window.clearInterval(retryTimer);
		retryTimer = null;
	}
};

/**
 * Reports the ODP customer/login event, once per browser session per person.
 *
 * Called from every point where the site identifies the user (the global nav, which mounts on
 * every CMS page, and the TCSM app). The session marker is what makes those call sites safe:
 * the CMS site is not an SPA, so the nav re-mounts on every page view, and the auth effects
 * also re-run on silent token renewal. Without it this would report a login continuously.
 */
export const registerOdpLogin = (personGuid: string): void => {
	// Must be a non-empty STRING. Both call sites derive the guid from a token claim and fall
	// back to returning the whole decoded-token object when the claim is absent, which would
	// otherwise send an object as _person_id and store "[object Object]" as the marker — the
	// latter never compares equal to a real guid, so the event would re-fire on every page.
	if (typeof window === 'undefined' || typeof personGuid !== 'string' || !personGuid) {
		return;
	}

	if (readMarker() === personGuid || pendingGuid === personGuid) {
		return;
	}

	if (sendLogin(personGuid)) {
		writeMarker(personGuid);
		return;
	}

	// GTM hasn't injected zaius-min.js yet — wait for it, but not indefinitely.
	pendingGuid = personGuid;
	let attempts = 0;
	retryTimer = window.setInterval(() => {
		attempts += 1;

		if (sendLogin(personGuid)) {
			writeMarker(personGuid);
			cancelRetry();
			return;
		}

		if (attempts >= MAX_ATTEMPTS) {
			cancelRetry();
		}
	}, RETRY_INTERVAL_MS);
};

/**
 * Clears the session marker so a subsequent sign-in reports a fresh login. Called from the
 * sign-out paths; without it, signing out and back in within the same browser session would
 * report nothing.
 */
export const clearOdpLoginMarker = (): void => {
	firedInThisDocument = null;

	if (typeof window === 'undefined') {
		pendingGuid = null;
		return;
	}

	// Cancel any retry still waiting for zaius, so it can't report a login after sign-out.
	cancelRetry();

	try {
		window.sessionStorage?.removeItem(FIRED_KEY);
	} catch { /* non-fatal */ }
};

/**
 * Resets the ODP browser identifier (the VUID cookie) so activity after sign-out is no longer
 * attributed to the person who just left. Matters most on shared devices: without it the next
 * visitor keeps browsing under the previous user's ODP identity.
 *
 * https://docs.developers.optimizely.com/optimizely-data-platform/docs/websdk-anonymize
 *
 * Not retried — unlike login, there is no useful "later" here: the sign-out paths navigate away
 * immediately. If zaius never loaded then nothing was ever identified, so there is nothing to
 * reset.
 */
export const anonymizeOdpUser = (): void => {
	const zaius = getZaius();
	if (!zaius?.anonymize) {
		return;
	}

	try {
		zaius.anonymize();
	} catch { /* non-fatal */ }
};

/**
 * The single call every sign-out path should make: forget that we reported a login, and reset
 * the ODP browser identity. Kept as one function so a future logout path can't wire up half of
 * it.
 *
 * Must be called synchronously at the start of sign-out, before the hosted-logout redirect —
 * both steps are synchronous, but nothing runs once the navigation commits.
 */
export const resetOdpOnLogout = (): void => {
	clearOdpLoginMarker();
	anonymizeOdpUser();
};
